import React, { useState, useCallback } from "react"
import { Select } from "antd"
import { DefaultOptionType } from "antd/es/select"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faGear,
  faChevronUp,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons"
import { TABLE_OPERATION_OPTIONS } from "features/Reporting/hooks/useOperatorManagement"
import { OperatorOption } from "types/ui.types"
import {
  STATISTIC_TYPE,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"

/**
 * Select option structure for dropdowns
 */
export interface SelectOption {
  /** Option value */
  value: string
  /** Display label */
  label: string
  /** Nested options for grouped selects */
  children?: SelectOption[]
}

/**
 * Props interface for ReportTableSettings component
 */
export interface ReportTableSettingsProps {
  /** If true, controls are always visible. If false, becomes collapsible */
  isVisible: boolean
  /** Options for variable selectors, typically from flattenCodebookForSelect */
  selectOptions: SelectOption[]
  /** Currently selected row variable paths */
  rowVariables: string[]
  /** State setter for row variables */
  setRowVariables: (variables: string[]) => void
  /** Currently selected column variable paths */
  columnVariables: string[]
  /** State setter for column variables */
  setColumnVariables: (variables: string[]) => void
  /** Currently selected operator strings (e.g., 'n', 'row%') */
  selectedOperators: STATISTIC_TYPE[]
  /** State setter for operators */
  setSelectedOperators: (operators: STATISTIC_TYPE[]) => void
  /** Available operator options with labels */
  operatorOptions: OperatorOption[]
  /** Current table operation type */
  tableOperationType?: TABLE_OPERATION_TYPE
  /** Callback for table operation type changes */
  onTableOperationTypeChange?: (type: TABLE_OPERATION_TYPE) => void
  /** Callback triggered when the 'Run' button is clicked */
  onRun: () => void
  /** Indicates if the 'Run' action is in progress */
  isLoading: boolean
}

/**
 * Component providing the user interface for selecting row variables,
 * column variables, and statistical operators for a report table.
 * It can be rendered as always visible or as a collapsible section.
 *
 * Features:
 * - Multiple variable selection with search/filter
 * - Statistical operator selection
 * - Table operation type selection (XTAB/DTAB)
 * - Collapsible interface when not in settings mode
 * - Form validation (requires at least one row and column variable)
 * - Loading states and accessibility support
 */
const ReportTableSettings: React.FC<ReportTableSettingsProps> = ({
  isVisible,
  selectOptions,
  rowVariables,
  setRowVariables,
  columnVariables,
  setColumnVariables,
  selectedOperators,
  setSelectedOperators,
  operatorOptions,
  tableOperationType = TABLE_OPERATION_TYPE.XTAB,
  onTableOperationTypeChange,
  onRun,
  isLoading,
}) => {
  // State to manage the collapsed/expanded state *only* when isVisible is false.
  const [showSelectionsWhenCollapsed, setShowSelectionsWhenCollapsed] =
    useState<boolean>(true)

  // Common filter logic for Ant Design Select components
  const filterOption = useCallback(
    (input: string, option?: DefaultOptionType): boolean =>
      // Check if the input text exists in the option's label (case-insensitive)
      (option?.label ?? "")
        .toString()
        .toLowerCase()
        .includes(input.toLowerCase()),
    [] // No dependencies, logic is self-contained
  )

  /**
   * Handle keyboard navigation for collapsible header
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>): void => {
      // Accessibility: Allow toggling with Enter or Space
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault() // Prevent default space scroll
        setShowSelectionsWhenCollapsed((prev) => !prev)
      }
    },
    []
  )

  /**
   * Toggle collapsed state
   */
  const toggleCollapsed = useCallback((): void => {
    setShowSelectionsWhenCollapsed((prev) => !prev)
  }, [])

  // The UI elements for selecting variables and operators
  const selectionControls = (
    <div
      // Use space-y for vertical spacing between elements
      // Apply mb-4 for margin below the controls block
      // Conditionally hide based on visibility props and internal state
      className={`space-y-4 mb-4 ${
        !isVisible && !showSelectionsWhenCollapsed ? "hidden" : ""
      }`}
    >
      {/* Table Operation Type Selector */}
      {onTableOperationTypeChange && (
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Table Operation Type
          </label>
          <Select<TABLE_OPERATION_TYPE>
            placeholder="Select operation type"
            className="w-full"
            value={tableOperationType}
            onChange={onTableOperationTypeChange}
            optionLabelProp="label"
            aria-label="Select table operation type"
          >
            {TABLE_OPERATION_OPTIONS.map((opt) => (
              <Select.Option
                key={opt.value}
                value={opt.value}
                label={opt.label}
              >
                <div>
                  <div style={{ fontWeight: "500" }}>{opt.label}</div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "2px",
                    }}
                  >
                    {opt.description}
                  </div>
                </div>
              </Select.Option>
            ))}
          </Select>
        </div>
      )}

      {/* Row Variables Selector */}
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-700">
          Row Variables
        </label>
        <Select<string[]>
          mode="multiple" // Allow multiple selections
          allowClear // Show clear button
          placeholder="Select row variables (at least one)"
          optionFilterProp="label" // Filter based on the displayed label
          className="w-full" // Take full width
          options={selectOptions} // Data source for options
          value={rowVariables} // Controlled component value
          onChange={setRowVariables} // Update state on change
          filterOption={filterOption} // Use the memoized filter function
          aria-label="Select row variables" // Accessibility
        />
      </div>

      {/* Column Variables Selector */}
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-700">
          Column Variables
        </label>
        <Select<string[]>
          mode="multiple"
          allowClear
          placeholder="Select column variables (at least one)"
          optionFilterProp="label"
          className="w-full"
          options={selectOptions}
          value={columnVariables}
          onChange={setColumnVariables}
          filterOption={filterOption}
          aria-label="Select column variables" // Accessibility
        />
      </div>

      {/* Operators Selector */}
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-700">
          Statistical Operators
        </label>
        <Select<STATISTIC_TYPE[]>
          mode="multiple"
          allowClear
          placeholder="Select operators (defaults selected)"
          className="w-full"
          options={operatorOptions}
          value={selectedOperators}
          onChange={setSelectedOperators}
          aria-label="Select statistical operators" // Accessibility
          // Note: No filterOption needed unless operator list is very long
        />
      </div>

      {/* Run Button */}
      <DataQButton
        type="primary" // Button style
        className="w-full" // Full width button
        onClick={onRun} // Trigger the passed-in run callback
        isLoading={isLoading} // Show loading state
        // Disable if variables aren't selected or if already loading
        isDisabled={
          rowVariables.length === 0 || columnVariables.length === 0 || isLoading
        }
        aria-label="Run Cross Tabulation" // Accessibility
      >
        Run Cross Tabulation
      </DataQButton>
    </div>
  )

  // If `isVisible` is true, always show the controls directly.
  if (isVisible) {
    return selectionControls
  }

  // If `isVisible` is false, render the collapsible header and the controls.
  // The visibility of controls is managed by `showSelectionsWhenCollapsed`.
  return (
    <>
      {/* Collapsible Header */}
      <div
        className="flex items-center gap-2 mb-2 p-3 shadow-sm rounded-lg cursor-pointer hover:bg-gray-100 transition-colors" // Original styling
        onClick={toggleCollapsed} // Toggle state on click
        role="button" // Accessibility: Indicate it's clickable
        tabIndex={0} // Accessibility: Make it focusable
        aria-expanded={showSelectionsWhenCollapsed} // Accessibility: Announce expanded state
        onKeyDown={handleKeyDown} // Handle keyboard navigation
      >
        <FontAwesomeIcon icon={faGear} className="w-4 h-4 text-[#36c3ed]" />
        <span className="text-sm text-gray-600 font-semibold">
          Table Settings
        </span>
        {/* Chevron indicating expanded/collapsed state */}
        <FontAwesomeIcon
          icon={showSelectionsWhenCollapsed ? faChevronUp : faChevronDown}
          className="w-4 h-4 text-gray-600 ml-auto" // Push chevron to the right
        />
      </div>
      {/* Render the selection controls (visibility handled by className) */}
      {selectionControls}
    </>
  )
}

export default ReportTableSettings
